import { useState } from 'react';
import type { ReactNode, UIEvent } from 'react';
import { ArrowLeft, X } from 'lucide-react';
import { cn, statusColor } from '@utils/index';

interface EntryPageShellProps {
  /** Status badge shown above the title (solid pill in the status color). */
  status?: string | null;
  title: string;
  subtitle?: string;
  onBack: () => void;
  /** Header icon for onBack: view pages keep 'arrow'; edit forms pass 'close'. */
  backIcon?: 'arrow' | 'close';
  /** Disables the back/close button, e.g. while a save is in flight. */
  backDisabled?: boolean;
  /** Right-aligned header actions (icon buttons on the blue band). */
  headerActions?: ReactNode;
  /** Tabs strip rendered under the title, inside the blue header. */
  tabs?: ReactNode;
  /**
   * Bottom action bar content, laid out justify-between (pass a left node and
   * a right node). Omit to render no bar (loading/error, print view).
   */
  footer?: ReactNode;
  /** id on the shell root - the print fence targets #palmetto-print-root. */
  rootId?: string;
  scrollClassName?: string;
  children: ReactNode;
}

/**
 * Entry page chrome shared by every view/edit page (same treatment as the
 * sibling Palmetto apps): big blue header with a status badge above the title
 * that collapses as the content scrolls, a scrollable body, and a bottom
 * action bar. The collapse is driven by this instance's own scroll container
 * - pages stay mounted in layers, so ids can't be trusted to be unique.
 */
const EntryPageShell = (props: EntryPageShellProps) => {
  const {
    status,
    title,
    subtitle,
    onBack,
    backIcon = 'arrow',
    backDisabled,
    headerActions,
    tabs,
    footer,
    rootId,
    scrollClassName,
    children,
  } = props;
  const [isScrolled, setIsScrolled] = useState(false);

  const handleScroll = (e: UIEvent<HTMLDivElement>) =>
    setIsScrolled(e.currentTarget.scrollTop > 10);

  return (
    <div id={rootId} className='rep:flex rep:h-full rep:w-full rep:flex-col rep:overflow-hidden'>
      {/* Big blue header */}
      <div
        className={cn(
          'rep:relative rep:shrink-0 rep:bg-[#0949EE] rep:transition-all rep:duration-300 rep:ease-in-out',
          isScrolled ? 'rep:pb-2' : 'rep:pb-0',
          tabs && 'rep:pb-0',
        )}>
        <button
          type='button'
          aria-label={backIcon === 'close' ? 'Close' : 'Back'}
          disabled={backDisabled}
          onClick={onBack}
          className={cn(
            'palmetto-print-hide rep:absolute rep:left-4 rep:cursor-pointer rep:rounded-full rep:p-2 rep:text-white rep:transition-all rep:duration-300 rep:hover:bg-white/20 rep:disabled:cursor-default rep:disabled:opacity-50',
            isScrolled ? 'rep:top-2' : 'rep:top-3',
          )}>
          {backIcon === 'close' ? (
            <X className='rep:size-5' />
          ) : (
            <ArrowLeft className='rep:size-5' />
          )}
        </button>

        {headerActions ? (
          <div
            className={cn(
              'palmetto-print-hide rep:absolute rep:right-4 rep:flex rep:items-center rep:gap-1 rep:transition-all rep:duration-300',
              isScrolled ? 'rep:top-2' : 'rep:top-3',
            )}>
            {headerActions}
          </div>
        ) : null}

        <div
          className={cn(
            'rep:flex rep:flex-col rep:items-start rep:pr-14 rep:pl-14 rep:transition-all rep:duration-300',
            isScrolled ? 'rep:pt-4 rep:pb-2' : 'rep:pt-12 rep:pb-4',
          )}>
          {status ? (
            <span
              className='rep:mb-1 rep:rounded-full rep:px-3 rep:py-0.5 rep:text-xs rep:font-semibold rep:text-white/87 rep:transition-all rep:duration-300'
              style={{ backgroundColor: statusColor(status) }}>
              {status}
            </span>
          ) : null}
          <h1
            className={cn(
              'rep:font-bold rep:text-white rep:transition-all rep:duration-300',
              isScrolled ? 'rep:text-base' : 'rep:text-xl',
            )}>
            {title}
          </h1>
          <p
            className={cn(
              'rep:text-white/80 rep:transition-all rep:duration-300',
              isScrolled ? 'rep:text-xs' : 'rep:text-sm',
            )}>
            {subtitle || ' '}
          </p>
        </div>
        {tabs ? <div className='palmetto-print-hide rep:px-4'>{tabs}</div> : null}
      </div>

      {/* Scrollable body. While at the top, the top 40px of the scroll area
          stays header-blue so overscroll reads as one surface. */}
      <div
        className={cn(
          'rep:relative rep:flex rep:min-h-0 rep:flex-1 rep:flex-col rep:overflow-hidden',
          !isScrolled &&
            'rep:bg-linear-to-b rep:from-[#0949EE] rep:from-[40px] rep:to-background rep:to-[40px]',
        )}>
        <div
          onScroll={handleScroll}
          className={cn(
            'rep:flex rep:h-full rep:w-full rep:flex-col rep:items-center rep:overflow-y-auto',
            scrollClassName,
          )}>
          <div
            aria-hidden
            className='rep:-mb-10 rep:h-10 rep:w-full rep:shrink-0 rep:bg-background'
          />
          {children}
          {/* Collapsing, the header gives the body 40px (48 with tabs, whose band
              keeps pb-0); this spacer hands the same height back to the content,
              on the header's 300ms transition, so the collapse never takes away
              the scroll that caused it. Without it, a body overflowing by 11-50px
              lost its overflow, snapped back under 10px and expanded the header
              again, bouncing instead of reaching its end. */}
          <div
            aria-hidden
            className={cn(
              'rep:shrink-0 rep:transition-all rep:duration-300',
              isScrolled ? (tabs ? 'rep:h-12' : 'rep:h-10') : 'rep:h-0',
            )}
          />
        </div>
      </div>

      {/* Bottom action bar */}
      {footer ? (
        <div className='palmetto-print-hide rep:flex rep:w-full rep:shrink-0 rep:flex-row rep:items-center rep:justify-center rep:overflow-hidden rep:bg-card rep:px-4 rep:py-2 rep:shadow'>
          <div className='rep:flex rep:w-full rep:max-w-353 rep:items-center rep:justify-between rep:px-4'>
            {footer}
          </div>
        </div>
      ) : null}
    </div>
  );
};

/** The standard content column every entry page stacks its sections into. */
export const EntryContent = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div
    className={cn('rep:flex rep:w-full rep:max-w-353 rep:flex-col rep:gap-4 rep:p-4', className)}>
    {children}
  </div>
);

/** Header icon button (white on the blue band). */
export const HeaderIconButton = ({
  label,
  onClick,
  children,
  disabled,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
  disabled?: boolean;
}) => (
  <button
    type='button'
    aria-label={label}
    title={label}
    disabled={disabled}
    onClick={onClick}
    className='rep:cursor-pointer rep:rounded-full rep:p-2 rep:text-white rep:transition-colors rep:hover:bg-white/20 rep:disabled:cursor-default rep:disabled:opacity-50'>
    {children}
  </button>
);

export default EntryPageShell;
